import { Application, Container } from 'pixi.js';
import { IntroScene } from './scenes/IntroScene.js';
import { StoryScene } from './scenes/StoryScene.js';
import { LocalStorySource } from './story/source.js';
import { RemoteStorySource } from './story/remoteSource.js';
import type { StorySource } from './story/source.js';
import { getTelegramWebApp, isInsideTelegram } from './telegram/webapp.js';

/** The minimal shape every swappable screen exposes to the app shell. */
interface Scene {
  readonly view: Container;
  update?(deltaMs: number): void;
  destroy(): void;
}

async function main(): Promise<void> {
  const telegram = getTelegramWebApp();
  telegram.ready();
  telegram.expand();

  if (!isInsideTelegram()) {
    console.info('[mini-app] running outside Telegram — using the stub WebApp bridge.');
  }

  const mount = document.getElementById('app');
  if (mount === null) throw new Error('Missing #app mount element');

  const app = new Application();
  await app.init({
    resizeTo: window,
    backgroundColor: 0x0b0f10,
    antialias: true,
  });
  mount.appendChild(app.canvas);

  let current: Scene;
  const show = (scene: Scene): void => {
    current.destroy(); // Container.destroy removes it from the stage too.
    current = scene;
    app.stage.addChild(scene.view);
  };

  const intro = await IntroScene.create(app);
  current = intro;
  app.stage.addChild(intro.view);
  intro.setOnStart(() => {
    // Pick the story source. With a configured game-api URL AND a real Telegram
    // initData, use the server-authoritative source (@brhack/game-api verifies
    // initData and runs the shared engine). Otherwise fall back to the local
    // animation preview (navigation only, no scoring) so the app still runs in
    // a plain browser. See apps/mini-app/README.md.
    const apiUrl = (import.meta as { env?: Record<string, string | undefined> }).env?.[
      'VITE_GAME_API_URL'
    ];
    const source: StorySource =
      apiUrl !== undefined && apiUrl.length > 0 && telegram.initData.length > 0
        ? new RemoteStorySource(apiUrl, telegram.initData)
        : new LocalStorySource();
    StoryScene.create(app, source)
      .then((story) => {
        show(story);
      })
      .catch((error: unknown) => {
        console.error('[mini-app] failed to open the case', error);
      });
  });

  app.ticker.add((ticker) => {
    current.update?.(ticker.deltaMS);
  });
}

main().catch((error: unknown) => {
  console.error('[mini-app] fatal startup error', error);
});
